import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";

export type TabDef = { id: string; label: ReactNode; count?: ReactNode };

/**
 * 页签。`line` = 对象页里分块的线式页签（选中深色字 + 蓝色下划线）；不带 = 视图页签（胶囊，选中浅蓝底）。
 * 键盘 ← → 切换（DESIGN.md §4.9）。面板由调用方渲染，id 用 `${idPrefix}-panel-${tab.id}`。
 * 视图页签筛的是同一张表、没有各自的面板（同样式包样张），不带 aria-controls：`line` 为 false 时不指向面板。
 * 卡片顶上的视图页签传 `className="viewbar"`：页签直接排在 .viewbar 里（同组件目录），外面不再包一层 .tabs。
 */
export function Tabs({ tabs, value, onChange, label, line = false, idPrefix = "tab", className = "" }: {
  tabs: TabDef[];
  value: string;
  onChange: (id: string) => void;
  label: string;
  line?: boolean;
  idPrefix?: string;
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: ReactKeyboardEvent, i: number) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length;
    const next = tabs[n];
    if (!next) return;
    refs.current[n]?.focus();
    onChange(next.id);
  };
  return (
    <div className={line ? `tabs tabs-line ${className}`.trim() : className || "tabs"} role="tablist" aria-label={label}>
      {tabs.map((t, i) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            className="tab"
            id={`${idPrefix}-${t.id}`}
            aria-controls={line ? `${idPrefix}-panel-${t.id}` : undefined}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.label}
            {t.count !== undefined && <span className="n">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 看大图（人脸、对话里的视觉帧）：整个窗口压暗，图按窗口放到最大、保持比例，不带标题和按钮，不出滚动条（汤祺 10-06）。
 * 点哪里都关（图本身、图外），按 Esc 也关；关掉后焦点回到点开它的缩略图（原生 <dialog> 自带）。
 * 挂到 body 下：缩略图在对话记录那一块的头一行里，留在原处会吃到那里的图片尺寸；
 * 点击也会冒泡到那一块、把它展开或收起——所以这里的点击不往上传。
 */
export function Lightbox({ src, alt, onClose }: { src: string | null; alt: string; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  // 图的宽高比：取到之前图先不露面，免得按原始尺寸闪一下
  const [ratio, setRatio] = useState<number | null>(null);
  useEffect(() => {
    setRatio(null);
    const d = ref.current;
    if (!d) return;
    if (src && !d.open) d.showModal();
    if (!src && d.open) d.close();
  }, [src]);
  return createPortal(
    // 点一下就关是鼠标的便利；键盘用 Esc（<dialog> 自带）
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      className="lightbox"
      aria-label={alt}
      onClose={onClose}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      {src && (
        <img
          src={src}
          alt={alt}
          className={ratio ? "is-ready" : undefined}
          style={ratio ? ({ "--ratio": ratio } as CSSProperties) : undefined}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth && img.naturalHeight) setRatio(img.naturalWidth / img.naturalHeight);
          }}
          // 图取不到：照样露出来（浏览器画一个坏图标和说明文字），不留一片空的暗底
          onError={() => setRatio(4 / 3)}
        />
      )}
    </dialog>,
    document.body,
  );
}

/**
 * 弹出菜单：原生 popover，位置贴着触发按钮（同样张 console.js 的 place）。
 * 返回给触发按钮和浮层各自的属性；鼠标点开焦点落在浮层本身，键盘打开落在第一项。
 */
export function usePopover(id: string, opts: { align?: "start" | "end"; side?: "bottom" | "top" } = {}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const lastKey = useRef(false);

  const place = useCallback(() => {
    const pop = popRef.current, btn = btnRef.current;
    if (!pop || !btn) return;
    const r = btn.getBoundingClientRect();
    const w = pop.offsetWidth, h = pop.offsetHeight;
    let left = opts.align === "end" ? r.right - w : r.left;
    let top = r.bottom + 6;
    if (opts.side === "top" || top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6);
    left = Math.min(Math.max(8, left), window.innerWidth - w - 8);
    pop.style.position = "fixed";
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [opts.align, opts.side]);

  useEffect(() => {
    const pop = popRef.current, btn = btnRef.current;
    if (!pop || !btn) return;
    const onToggle = (e: Event) => {
      const open = (e as ToggleEvent).newState === "open";
      btn.setAttribute("aria-expanded", String(open));
      if (!open) return;
      place();
      if (lastKey.current) {
        pop.querySelector<HTMLElement>("a,button,input")?.focus({ preventScroll: true });
      } else {
        pop.tabIndex = -1;
        pop.focus({ preventScroll: true });
      }
    };
    // 菜单里 ↑ ↓ 在各项之间移动
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const items = [...pop.querySelectorAll<HTMLElement>(".menu-i:not([aria-disabled='true'])")];
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement as HTMLElement);
      const n = e.key === "ArrowDown" ? items[(i + 1) % items.length] : items[(i - 1 + items.length) % items.length];
      n?.focus();
    };
    pop.addEventListener("toggle", onToggle);
    pop.addEventListener("keydown", onKeyDown);
    return () => {
      pop.removeEventListener("toggle", onToggle);
      pop.removeEventListener("keydown", onKeyDown);
    };
  }, [place]);

  return {
    trigger: {
      ref: btnRef,
      popoverTarget: id,
      "aria-haspopup": "menu" as const,
      "aria-expanded": false,
      onPointerDown: () => { lastKey.current = false; },
      onKeyDown: (e: ReactKeyboardEvent) => {
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") lastKey.current = true;
        if (e.key === "ArrowDown") { e.preventDefault(); popRef.current?.showPopover(); place(); }
      },
    },
    popover: { ref: popRef, id, popover: "auto" as const, role: "menu" },
    close: () => popRef.current?.hidePopover(),
  };
}
